import DeleteIcon from "@mui/icons-material/Delete";
import {
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Radio,
    RadioGroup,
    Stack,
    TextField,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IAssignedPostcode } from "../../interfaces/IAssignedPostcode";
import { getFetch, useFetch } from "../../hooks/useFetch";
import { start } from "repl";
import { IProfile } from "../../interfaces/IUser";

interface IProps {
    assignedPlzs?: IAssignedPostcode[];
    setAssignedPlzs: (plz: IAssignedPostcode[]) => void;
}

export const ProfilePlzAssignment: React.FC<IProps> = ({ assignedPlzs, setAssignedPlzs }) => {
    const [plzInputMethod, setPlzInputMethod] = useState("single");
    const [plzFirst, setPlzFirst] = useState("");
    const [plzSecond, setPlzSecond] = useState("");
    const [plzFirstError, setPlzFirstError] = useState("");
    const [plzSecondError, setPlzSecondError] = useState("");
    const [plzChecked, setPlzChecked] = useState(false);
    const [plzCheckedError, setPlzCheckedError] = useState(false);

    const handlePLZChange = useCallback(
        (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            valueSetter: (value: string) => void,
            errorSetter: (value: string) => void,
            options?: {
                allowWildcards?: boolean;
            },
        ) => {
            const value = event.target.value;
            valueSetter(event.target.value);

            if (value === "") {
                errorSetter("");
                setPlzCheckedError(false);
            } else if (options?.allowWildcards && !/^[\d*]+$/.test(value)) {
                errorSetter("Die PLZ darf nur Zahlen und '*' enthalten.");
                setPlzCheckedError(false);
            } else if (!options?.allowWildcards && !/^\d+$/.test(value)) {
                errorSetter("Die PLZ darf nur Zahlen enthalten.");
                setPlzCheckedError(false);
            } else if (value.length < 5) {
                errorSetter("Die PLZ muss 5 Zeichen lang sein.");
                setPlzCheckedError(false);
            } else if (value.length > 5) {
                errorSetter("Die PLZ darf maximal 5 Zeichen lang sein.");
                setPlzCheckedError(false);
            } else {
                errorSetter("");
                setPlzCheckedError(true);
            }
        },
        [],
    );

    const handlePLZCheck = useCallback(
        (postcodes: IAssignedPostcode[]) => {
            if (postcodes.length > 0) {
                for (const plz of postcodes) {
                    //check which plz causes the error

                    if (plz.start <= plzFirst && plzFirst <= plz.end) {
                        setPlzFirstError(
                            "Diese PLZ liegt in einem bereits vergebenen Bereich für den Mitarbeiter mit der ID " +
                                plz.profile,
                        );
                        setPlzChecked(false);
                        return;
                    } else if (plzInputMethod === "range" && plz.start <= plzSecond && plzSecond <= plz.end) {
                        setPlzSecondError(
                            "Diese PLZ liegt in einem bereits vergebenen Bereich für den Mitarbeiter mit der ID " +
                                plz.profile,
                        );
                        setPlzChecked(false);
                        return;
                    }
                }
                setPlzChecked(false);
            } else {
                setPlzChecked(true);
                setPlzFirstError("");
                setPlzSecondError("");
            }
        },
        [setPlzChecked, setPlzFirstError, setPlzSecondError, plzInputMethod, plzFirst, plzSecond],
    );

    const checkPLZExists = useCallback(
        async (start: string, end: string, exludeId?: number) => {
            //check if the start is smaller than the end
            if (start > end && plzInputMethod === "range") {
                setPlzFirstError("Die PLZ von muss kleiner sein als die PLZ bis.");
                setPlzSecondError("Die PLZ bis muss größer sein als die PLZ von.");
                setPlzChecked(false);
                return;
            }
            //check the assigned plzs if they are already assigned
            for (const plz of assignedPlzs || []) {
                if (plz.start <= start && start <= plz.end) {
                    setPlzFirstError("Diese PLZ wurde für diesen Mitarbeiter bereits vergeben.");
                    setPlzChecked(false);
                    return;
                } else if (plzInputMethod === "range" && plz.start <= end && end <= plz.end) {
                    setPlzSecondError("Diese PLZ wurde für diesen Mitarbeiter bereits vergeben.");
                    setPlzChecked(false);
                    return;
                }
            }
            getFetch<IAssignedPostcode[]>(
                `/postcode/assignment/exists?start=${start}&end=${end}${exludeId ? `&excludeId=${exludeId}` : ""}`,
                null,
                handlePLZCheck,
            );
        },
        [handlePLZCheck, plzInputMethod, setPlzFirstError, setPlzSecondError, setPlzChecked],
    );

    useEffect(() => {
        if (plzFirst.length === 5 && plzSecond.length === 5 && plzInputMethod === "range" && plzCheckedError) {
            checkPLZExists(plzFirst, plzSecond);
        } else if (plzFirst.length === 5 && plzInputMethod === "single" && plzCheckedError) {
            checkPLZExists(plzFirst, plzFirst);
        } else {
            setPlzChecked(false);
        }
    }, [plzFirst, plzSecond, checkPLZExists, plzInputMethod]);

    return (
        <Stack direction={"row"} sx={{ width: "100%" }} spacing={2}>
            <FormControl fullWidth>
                <FormLabel>PLZ(s) zuordnen</FormLabel>
                <RadioGroup
                    defaultValue={"single"}
                    value={plzInputMethod}
                    onChange={(event) => {
                        setPlzInputMethod(event.target.value);
                        setPlzFirstError("");
                        setPlzSecondError("");
                        setPlzFirst("");
                        setPlzSecond("");
                    }}
                >
                    <Stack direction={"row"}>
                        <FormControlLabel value="single" control={<Radio />} label="Einzeln" />
                        <FormControlLabel value="range" control={<Radio />} label="Bereich" />
                    </Stack>
                </RadioGroup>
                <Stack direction={"row"} spacing={2}>
                    {plzInputMethod === "single" ? (
                        <TextField
                            size="small"
                            fullWidth
                            id="plz-input"
                            label="PLZ"
                            value={plzFirst}
                            error={plzFirstError !== ""}
                            helperText={plzFirstError}
                            onChange={(event) => {
                                handlePLZChange(event, setPlzFirst, setPlzFirstError, { allowWildcards: true });
                            }}
                        />
                    ) : (
                        <Stack direction={"row"} sx={{ width: "100%" }} spacing={2}>
                            <TextField
                                size="small"
                                sx={{ width: "100%" }}
                                id="plz-input"
                                label="PLZ von"
                                error={plzFirstError !== ""}
                                helperText={plzFirstError}
                                value={plzFirst}
                                onChange={(event) => {
                                    handlePLZChange(event, setPlzFirst, setPlzFirstError, { allowWildcards: false });
                                }}
                            />
                            <TextField
                                size="small"
                                sx={{ width: "100%" }}
                                id="plz-input"
                                label="PLZ bis"
                                error={plzSecondError !== ""}
                                helperText={plzSecondError}
                                value={plzSecond}
                                onChange={(event) => {
                                    handlePLZChange(event, setPlzSecond, setPlzSecondError, { allowWildcards: false });
                                }}
                            />
                        </Stack>
                    )}
                    <Button
                        variant="contained"
                        disabled={!plzChecked}
                        sx={{ px: "2rem", height: "40px" }}
                        onClick={() => {
                            setAssignedPlzs([
                                ...(assignedPlzs || []),
                                plzInputMethod === "single"
                                    ? { start: plzFirst, end: plzFirst }
                                    : { start: plzFirst, end: plzSecond },
                            ]);
                        }}
                    >
                        Hinzufügen
                    </Button>
                </Stack>
            </FormControl>
            <Stack spacing={1} sx={{ width: "100%" }}>
                <p>Zugeordnete PLZs</p>
                <List
                    sx={{
                        width: "100%",
                        bgcolor: "#efefef",
                        borderRadius: "5px",
                        pt: "4px",
                        maxHeight: 260,
                        overflow: "auto",
                    }}
                    dense
                    disablePadding
                >
                    {assignedPlzs &&
                        assignedPlzs.map((plz, i, arr) => (
                            <ListItem
                                key={`${plz.start}-${plz.end}`}
                                dense
                                disablePadding
                                sx={{
                                    px: "1rem",
                                    borderBottom: i === arr.length - 1 ? "" : "1px #adadad solid",
                                }}
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        size="small"
                                        onClick={() => {
                                            setAssignedPlzs(assignedPlzs.filter((_, index) => index !== i));
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                }
                            >
                                <ListItemText disableTypography>
                                    <p
                                        style={{
                                            fontSize: "14px",
                                            lineHeight: "14px",
                                            margin: 0,
                                            padding: "8px 0 8px 0",
                                        }}
                                    >
                                        {plz.start === plz.end ? `${plz.start}` : `${plz.start} - ${plz.end}`}
                                    </p>
                                </ListItemText>
                            </ListItem>
                        ))}
                </List>
            </Stack>
        </Stack>
    );
};
